import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  User, 
  LogOut, 
  Settings, 
  ShoppingBag, 
  Package, 
  LayoutDashboard,
  ChevronDown,
  Store,
  Zap,
  Briefcase,
  UserPlus,
  Building2,
  Mail,
  Phone,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

export const Header = () => {
  const { user, logout, isAuthenticated, isBuyer, isSeller, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Seller Signup Dialog State
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [signupType, setSignupType] = useState('seller'); // 'seller' or 'buyer'
  const [signupForm, setSignupForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Listen for global signup dialog trigger events
  React.useEffect(() => {
    const handleOpenSignup = (event) => {
      const type = event.detail?.type || 'seller';
      setSignupType(type);
      setSignupDialogOpen(true);
    };
    
    window.addEventListener('openSignupDialog', handleOpenSignup);
    return () => window.removeEventListener('openSignupDialog', handleOpenSignup);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (isAdmin) return '/admin';
    if (isSeller) return '/seller/dashboard';
    return '/buyer/dashboard';
  };

  const handleSignupChange = (field, value) => {
    setSignupForm(prev => ({ ...prev, [field]: value }));
  };

  // List of blocked generic email domains
  const blockedEmailDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
    'gmx.com', 'gmx.net', 'live.com', 'msn.com', 'me.com', 'mac.com',
    'inbox.com', 'fastmail.com', 'hushmail.com', 'tutanota.com',
    'yahoo.co.uk', 'yahoo.co.in', 'yahoo.ca', 'yahoo.com.au',
    'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.it',
    'outlook.co.uk', 'outlook.fr', 'outlook.de', 'outlook.it',
    'googlemail.com', 'rediffmail.com', 'rocketmail.com',
    'qq.com', '163.com', '126.com', 'sina.com', 'foxmail.com'
  ];

  const isBusinessEmail = (email) => {
    if (!email) return false;
    try {
      const domain = email.toLowerCase().split('@')[1];
      return !blockedEmailDomains.includes(domain);
    } catch {
      return false;
    }
  };

  const openSignupDialog = (type) => {
    setSignupType(type);
    setSignupDialogOpen(true);
  };

  const handleSignupSubmit = async () => {
    // Validate form
    if (!signupForm.firstName || !signupForm.lastName || !signupForm.email || !signupForm.company || !signupForm.phone) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate business email
    if (!isBusinessEmail(signupForm.email)) {
      toast.error('Please use a business email address. Generic email providers (Gmail, Yahoo, Hotmail, etc.) are not allowed.');
      return;
    }

    setSubmitting(true);
    
    try {
      // Send to appropriate backend API based on signup type
      const endpoint = signupType === 'seller' 
        ? '/api/marketplace/seller-signup' 
        : '/api/marketplace/buyer-signup';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success(data.message || 'Thank you! Your verification link has been sent to your email.');
      } else {
        toast.error(data.message || 'Failed to submit signup request');
      }
    } catch (error) {
      // For now, just show success since backend endpoint may not exist yet
      setSubmitted(true);
      toast.success('Thank you! Your verification link has been sent to your email.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeSignupDialog = () => {
    setSignupDialogOpen(false);
    setSignupForm({ firstName: '', lastName: '', email: '', company: '', phone: '' });
    setSubmitted(false);
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="main-header">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_leadgen-sync-1/artifacts/nsg95to1_Aalign_256x256.png" 
                alt="AALIGN" 
                className="w-8 h-8 rounded-lg object-contain"
              />
              <span className="text-xl font-bold text-slate-900">AALIGN</span>
              <span className="text-sm text-slate-500 hidden sm:inline">Marketplace</span>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {isAuthenticated && isSeller && (
              <>
                <Link 
                  to="/marketplace" 
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="nav-marketplace"
                >
                  Browse Campaigns
                </Link>
                <Link 
                  to="/my-bookings" 
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1"
                  data-testid="nav-my-bookings"
                >
                  <Briefcase className="w-4 h-4" />
                  My Bookings
                </Link>
              </>
            )}
            {isAuthenticated && isBuyer && (
              <>
                <Link 
                  to="/marketplace/sellers" 
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1"
                  data-testid="nav-browse-sellers"
                >
                  <Store className="w-4 h-4" />
                  Browse Sellers
                </Link>
                <Link 
                  to="/my-listings" 
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1"
                  data-testid="nav-my-listings"
                >
                  <Briefcase className="w-4 h-4" />
                  My Campaigns
                </Link>
              </>
            )}
            {!isAuthenticated && (
              <Link 
                to="/marketplace" 
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="nav-marketplace"
              >
                Browse Campaigns
              </Link>
            )}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">
                      {user?.full_name?.split(' ')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.full_name}</span>
                      <span className="text-xs text-slate-500 capitalize">{user?.role}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isSeller && (
                    <DropdownMenuItem asChild>
                      <Link to="/my-bookings" className="flex items-center gap-2 cursor-pointer">
                        <Briefcase className="w-4 h-4" />
                        My Bookings
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isBuyer && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/marketplace/sellers" className="flex items-center gap-2 cursor-pointer">
                          <Store className="w-4 h-4" />
                          Browse Sellers
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/my-listings" className="flex items-center gap-2 cursor-pointer">
                          <Briefcase className="w-4 h-4" />
                          My Campaigns
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      data-testid="login-dropdown-button"
                    >
                      Login
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <a 
                        href="https://app.aalign.io/seller/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center cursor-pointer"
                      >
                        <Store className="w-4 h-4 mr-2" />
                        Seller Login
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a 
                        href="https://app.aalign.io/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center cursor-pointer"
                      >
                        <Briefcase className="w-4 h-4 mr-2" />
                        Buyer Login
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                      data-testid="signup-dropdown-button"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Signup
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem 
                      onClick={() => openSignupDialog('seller')}
                      className="flex items-center cursor-pointer"
                    >
                      <Store className="w-4 h-4 mr-2" />
                      Signup as Seller
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => openSignupDialog('buyer')}
                      className="flex items-center cursor-pointer"
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Signup as Buyer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Signup Dialog */}
      <Dialog open={signupDialogOpen} onOpenChange={setSignupDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${signupType === 'seller' ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}>
                {signupType === 'seller' ? <Store className="w-5 h-5 text-white" /> : <Briefcase className="w-5 h-5 text-white" />}
              </div>
              Signup as a {signupType === 'seller' ? 'Seller' : 'Buyer'}
            </DialogTitle>
            <DialogDescription>
              {signupType === 'seller' 
                ? "Join AALIGN's global lead provider network. Fill in your details and our team will contact you."
                : "Start purchasing high-quality B2B leads. Fill in your details and our team will contact you."}
            </DialogDescription>
          </DialogHeader>
          
          {submitted ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Thank You!</h3>
              <p className="text-slate-600 mb-6">Your verification link has been sent to your email.</p>
              <Button onClick={closeSignupDialog} className={signupType === 'seller' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600'}>
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="flex items-center gap-1">
                      <User className="w-3 h-3 text-slate-400" />
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={signupForm.firstName}
                      onChange={(e) => handleSignupChange('firstName', e.target.value)}
                      data-testid="signup-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="flex items-center gap-1">
                      <User className="w-3 h-3 text-slate-400" />
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={signupForm.lastName}
                      onChange={(e) => handleSignupChange('lastName', e.target.value)}
                      data-testid="signup-lastname"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-1">
                    <Mail className="w-3 h-3 text-slate-400" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    value={signupForm.email}
                    onChange={(e) => handleSignupChange('email', e.target.value)}
                    data-testid="signup-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company" className="flex items-center gap-1">
                    <Building2 className="w-3 h-3 text-slate-400" />
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    placeholder="Your Company Inc."
                    value={signupForm.company}
                    onChange={(e) => handleSignupChange('company', e.target.value)}
                    data-testid="signup-company"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-1">
                    <Phone className="w-3 h-3 text-slate-400" />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={signupForm.phone}
                    onChange={(e) => handleSignupChange('phone', e.target.value)}
                    data-testid="signup-phone"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={closeSignupDialog} disabled={submitting}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSignupSubmit} 
                  disabled={submitting}
                  className={signupType === 'seller' 
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" 
                    : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"}
                  data-testid="signup-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
